import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { Button } from '@/components/atoms'

import * as api from '@/apis/aipass'
import { ButtonType } from '@/components/atoms/button'
import { modalContainerStyle } from '@/components/pages/accommodation-management/list-layout'
import { useCashChanger } from '@/hooks/use-cash-changer'
import { cashChangerStorageKey } from '@/constants/self-checkin-device-storage'
import { LoaderContextCreator } from '@/contexts/loader'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { toErrorMessage } from '@/libs/errors'
import { PaymentResultByCash } from '@/models/self-checkin'
import { createSelfCheckinResponsiveStyle, FrontTabletResponsiveToken } from '@/utils/front-tablet-responsive-style'

type Props = {
  isVisibility: boolean
  hotelId: string
  totalPrice: number
  doPayment: () => Promise<PaymentResultByCash>
  onCancel: () => void
  onComplete: (paymentResult: PaymentResultByCash) => void
}

export const CalcCashChangerModal: React.FC<Props> = props => {
  const { t } = useTranslation()
  const cashChanger = useCashChanger()
  const { errorHandler } = useErrorHandler()
  const { setIsLoading } = useContext(LoaderContextCreator())

  const [depositPrice, setDepositPrice] = useState(0)
  const [announceText, setAnnounceText] = useState('')
  const [connector, setConnector] = useState<{ connection: any; device: any }>()
  const [paymentResult, setPaymentResult] = useState<PaymentResultByCash>()
  const [isConfirmedDeposit, setIsConfirmedDeposit] = useState(false)
  const [isCompletedDeposit, setIsCompletedDeposit] = useState(false)
  const [isConnectProcessing, setIsConnectProcessing] = useState(false)
  const [isDeviceAvailable, setIsDeviceAvailable] = useState(false)
  // beginDeposit 後の初回 onDeposit までは釣銭機が受付状態か判断できないため、キャンセル操作を受け付けない
  const [isWaitingFirstDeposit, setIsWaitingFirstDeposit] = useState(false)

  const connectionTimer = useRef<any>(null)
  const deviceConnectionStatusTimer = useRef<any>(null)
  // 精算後は入金確定済みのため、切断・キャンセル時の返金(DEPOSIT_REPAY)を抑止する
  const isSettledDeposit = useRef(false)
  // beginDeposit 後に投入が始まっていない状態で DEPOSIT_REPAY を送ると釣銭機がエラーになるため、入金開始を検知して返金対象を判定する
  const isStartedDeposit = useRef(false)

  const doPayment = async () => {
    if (!connector) {
      setAnnounceText('Communication failed')
      return
    }
    try {
      setIsLoading(true)
      const paymentResult = await props.doPayment()
      setPaymentResult(paymentResult)
      api.cashChangerLog({ message: 'doPayment', changePrice, depositPrice, totalPrice: props.totalPrice })
      isSettledDeposit.current = true
      connector.device.endDeposit(changePrice ? connector.device.DEPOSIT_CHANGE : connector.device.DEPOSIT_NOCHANGE)
    } catch (e) {
      setIsLoading(false)
      errorHandler(e)
    }
  }

  const deviceComplete = (_paymentResult: PaymentResultByCash) => {
    setTimeout(() => {
      setIsLoading(false)
      // 釣り銭処理中にデストラクタが動作しないようにするバッファ1000ms
      props.onComplete(_paymentResult)
    }, 1000)
  }

  const onDispense = (data: { status: 'SUCCESS' | string }) => {
    api.cashChangerLog({ message: 'onDispense', data })
    if (!paymentResult) {
      return
    }
    if (data.status !== 'SUCCESS') {
      alert(`${t('Change is insufficient.')}\n${t('Please contact the front staff.')}`)
      connector!.device.dispenseChange(changePrice)
      return
    }
    deviceComplete(paymentResult)
    return
  }

  const onStatusUpdate = data => {
    if (deviceConnectionStatusTimer.current) {
      clearTimeout(deviceConnectionStatusTimer.current)
    }
    api.cashChangerLog({ message: 'onStatusUpdate', data })
    setIsDeviceAvailable(true)
  }

  const onDeposit = (data: {
    status: 'BUSY' | 'PAUSE' | 'END' | 'CASH_IN_TRAY_ERROR' | 'REJECT_UNIT_ERROR' | 'COMMAND_ERROR' | 'DEVICE_ERROR' | 'SYSTEM_ERROR'
    amount: string
  }) => {
    api.cashChangerLog({ message: 'onDeposit', data })
    setIsWaitingFirstDeposit(false)
    if (data.status === 'END') {
      setIsConfirmedDeposit(true)
    } else if (data.status !== 'BUSY') {
      setAnnounceText(`${t('Payment cannot be accepted.')}\n${t('Please contact the front staff.')}`)
    } else {
      isStartedDeposit.current = true
      setAnnounceText(t('Please insert bills and coins'))
    }
    setDepositPrice(Number(data.amount))
  }

  const connectDevice = async () => {
    try {
      if (connector) {
        return
      }
      setAnnounceText(t('Connecting to device'))
      setIsConnectProcessing(true)
      const _connector = await cashChanger.initialize({
        hotelId: props.hotelId,
        serialNumber: localStorage.getItem(cashChangerStorageKey) || '',
      })
      connectionTimer.current = setTimeout(() => {
        api.cashChangerLog({ message: 'onConnectionTimer' })
        disconnectDevice(_connector)
      }, 3000)
      setConnector(_connector)
      api.cashChangerLog({ message: 'success initialize' })
      _connector.device.onstatusupdate = onStatusUpdate
      return _connector
    } catch (error: any) {
      api.cashChangerLog({ message: toErrorMessage(error) })
      setAnnounceText(toErrorMessage(error) || '釣銭機に接続できません')
    } finally {
      setIsConnectProcessing(false)
    }
  }
  // 入金開始済みかつ未精算の場合のみ、投入済みの現金を返金する
  const shouldRepayDeposit = () => isStartedDeposit.current && !isSettledDeposit.current

  const disconnectDevice = async (_connector: { connection: any; device: any }) => {
    if (_connector.device) {
      api.cashChangerLog({ message: shouldRepayDeposit() ? 'disconnectDeviceAndRepay' : 'disconnectDevice' })
      _connector.device.ondeposit = undefined
      _connector.device.ondispense = undefined
      _connector.device.onstatusupdate = undefined
      if (shouldRepayDeposit()) {
        _connector.device.endDeposit(_connector.device.DEPOSIT_REPAY)
      }
    }
    cashChanger.disconnect(_connector.connection, _connector.device)
    setConnector(undefined)
    setIsDeviceAvailable(false)
  }

  const changePrice = useMemo(() => Math.max(depositPrice - props.totalPrice, 0), [depositPrice])
  const isEnoughPayment = useMemo(() => depositPrice >= props.totalPrice, [depositPrice])

  useEffect(() => {
    if (!props.isVisibility || isConnectProcessing) {
      return
    }
    let timeoutId: ReturnType<typeof setTimeout> | undefined

    if (!connector) {
      timeoutId = setTimeout(() => connectDevice(), 3000)
    } else if (!isDeviceAvailable) {
      if (deviceConnectionStatusTimer.current) {
        clearTimeout(deviceConnectionStatusTimer.current)
      }
      deviceConnectionStatusTimer.current = setTimeout(() => {
        api.cashChangerLog({ message: 'onDeviceConnectionStatusTimer' })
        disconnectDevice(connector)
      }, 5000)
    } else {
      connector.device.ondeposit = onDeposit
      setIsWaitingFirstDeposit(true)
      connector.device.beginDeposit()
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [isDeviceAvailable, connector, props.isVisibility, isConnectProcessing])

  useEffect(() => {
    const isSuccess = paymentResult && isConfirmedDeposit && !isCompletedDeposit
    if (!isSuccess) {
      return
    }
    setIsCompletedDeposit(true)
    if (changePrice) {
      // 入金額確定チェック完了後のお釣り返却
      api.cashChangerLog({ message: 'onSuccess', changePrice })
      connector!.device.ondispense = onDispense
      connector!.device.dispenseChange(changePrice)
      return
    }
    api.cashChangerLog({ message: 'onSuccess' })
    deviceComplete(paymentResult)
  }, [paymentResult, isConfirmedDeposit, isCompletedDeposit])

  useEffect(() => {
    if (connector) {
      clearTimeout(connectionTimer.current)
    }
    return () => {
      if (!connector) {
        return
      }
      disconnectDevice(connector)
    }
  }, [connector])

  useEffect(() => {
    if (localStorage.getItem(cashChangerStorageKey)) {
      connectDevice()
    }
  }, [])

  return (
    <div css={modalContainerStyle} style={{ display: 'flex', visibility: props.isVisibility ? 'visible' : 'hidden' }}>
      <div css={modalBodyContainerStyle}>
        <div css={modalHeaderStyle}>{t('Payment by cash')}</div>
        <div css={modalMainContainerStyle}>
          <div css={contentStyle}>
            <div className="register-container">
              <div className="price-block">
                <div>{t('Amount of money')}</div>
                <div>¥{props.totalPrice.toLocaleString()}</div>
              </div>
              <div className="price-block">
                <div>{t('Deposit amount')}</div>
                <div>¥{depositPrice.toLocaleString()}</div>
              </div>
              <div className="price-block">
                <div>{t('SelfCheckinPayment.Change')}</div>
                <div>¥{changePrice.toLocaleString()}</div>
              </div>
            </div>
            <div className="announce-text">{isEnoughPayment ? t('Please tap the “Complete” button') : announceText}</div>
          </div>
        </div>
        <div css={modalButtonSectionStyle}>
          <Button
            width={160}
            height={60}
            buttonType={isWaitingFirstDeposit ? ButtonType.BorderDisabled : ButtonType.NonStyle}
            isDisabled={isWaitingFirstDeposit}
            onClick={() => {
              if (connector?.device && shouldRepayDeposit()) {
                api.cashChangerLog({ message: 'onCancelAndRepay' })
                connector.device.ondeposit = undefined
                isStartedDeposit.current = false
                connector.device.endDeposit(connector.device.DEPOSIT_REPAY)
              }
              props.onCancel()
            }}
            css={modalButtonStyle}
          >
            {t('Cancel')}
          </Button>
          <Button
            width={160}
            height={60}
            buttonType={isEnoughPayment ? ButtonType.Primary : ButtonType.PrimaryDisabled}
            isDisabled={!isEnoughPayment}
            onClick={doPayment}
            css={modalButtonStyle}
          >
            {t('SelfCheckinPayment.Complete”')}
          </Button>
        </div>
      </div>
    </div>
  )
}

const contentStyle = css({
  background: 'white',
  width: '100%',
  borderRadius: 5,
  padding: '32px 32px 24px 27px',
  display: 'flex',
  flexDirection: 'column',
  gap: 24,
  '.register-container': {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    '.price-block': {
      fontWeight: 'bold',
      display: 'flex',
      justifyContent: 'space-between',
      ...createSelfCheckinResponsiveStyle({
        landscape11: { fontSize: FrontTabletResponsiveToken.bodyFontSize.landscape11 },
        portrait: { fontSize: FrontTabletResponsiveToken.bodyFontSize.portrait },
        landscape13: { fontSize: FrontTabletResponsiveToken.bodyFontSize.landscape13 },
      }),
    },
  },
  '.announce-text': {
    color: '#f2a40b',
    textAlign: 'center',
    wordBreak: 'keep-all',
    ...createSelfCheckinResponsiveStyle({
      landscape11: { fontSize: FrontTabletResponsiveToken.bodyFontSize.landscape11 },
      portrait: { fontSize: FrontTabletResponsiveToken.bodyFontSize.portrait },
      landscape13: { fontSize: FrontTabletResponsiveToken.bodyFontSize.landscape13 },
    }),
  },
})

const modalBodyContainerStyle = css(
  {
    position: 'relative',
    scale: '1.2',
    margin: 'auto',
    zIndex: 102,
    opacity: 1,
    background: '#FFF',
    borderRadius: 5,
  },
  createSelfCheckinResponsiveStyle({
    landscape11: { width: 590 },
    portrait: { width: 590 },
    landscape13: { width: 683 },
  }),
)

const modalHeaderStyle = css(
  {
    height: 40,
    margin: '16px 40px',
    fontWeight: 'bold',
    color: '#272727',
  },
  createSelfCheckinResponsiveStyle({
    landscape11: { fontSize: 22, lineHeight: 1.7 },
    portrait: { fontSize: 22, lineHeight: 1.7 },
    landscape13: { fontSize: 26, lineHeight: 1.5 },
  }),
)

const modalMainContainerStyle = css({
  padding: 32,
  backgroundColor: '#F2F2F2',
  display: 'flex',
})

const modalButtonSectionStyle = css({
  display: 'flex',
  padding: '16px 32px',
  justifyContent: 'space-between',
})

const modalButtonStyle = css(
  {
    borderRadius: 30,
  },
  createSelfCheckinResponsiveStyle({
    landscape11: { fontSize: FrontTabletResponsiveToken.standardButtonFontSize.landscape11 },
    portrait: { fontSize: FrontTabletResponsiveToken.standardButtonFontSize.portrait },
    landscape13: { fontSize: FrontTabletResponsiveToken.standardButtonFontSize.landscape13 },
  }),
)
